import React, { useState } from "react";
import { FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { extractError } from "../lib/api";
import { downloadApiFile, openApiPrintView } from "../lib/downloadApiFile";

/**
 * Weight slip Print / PDF with two modes: weight only, or weight + loading/unloading.
 */
export default function WeighmentSlipActions({
  printPath,
  pdfPath,
  pdfFilenameBase,
  opsKind = "loading",
  compact = false,
  testIdPrefix = "weighment",
  stopPropagation = false,
}) {
  const [busy, setBusy] = useState(false);
  const opsLabel = opsKind === "unloading" ? "unloading" : "loading";
  const fullPrintLabel = `Weight + ${opsLabel}`;
  const fullPdfLabel = `PDF with ${opsLabel}`;

  const run = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const printWeight = () =>
    run(() => openApiPrintView(printPath, { include_ops: false }));
  const printFull = () =>
    run(() => openApiPrintView(printPath, { include_ops: true }));
  const pdfWeight = () =>
    run(async () => {
      await downloadApiFile(pdfPath, `${pdfFilenameBase}-weight.pdf`, { include_ops: false });
      toast.success("Weight slip PDF downloaded");
    });
  const pdfFull = () =>
    run(async () => {
      await downloadApiFile(pdfPath, `${pdfFilenameBase}-full.pdf`, { include_ops: true });
      toast.success(`Weight + ${opsLabel} PDF downloaded`);
    });

  const wrapClick = (handler) => (e) => {
    if (stopPropagation) e?.stopPropagation?.();
    handler();
  };

  const triggerClass = compact
    ? "h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
    : "text-xs h-8 shrink-0 whitespace-nowrap px-2.5";

  return (
    <div
      className="flex items-center justify-end gap-0.5 shrink-0"
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
      onKeyDown={stopPropagation ? (e) => e.stopPropagation() : undefined}
      role="presentation"
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size={compact ? "icon" : "sm"}
            variant={compact ? "ghost" : "outline"}
            className={triggerClass}
            disabled={busy}
            title="Print weight slip"
            aria-label="Print weight slip"
            data-testid={`print-${testIdPrefix}`}
          >
            <Printer className="h-4 w-4" />
            {!compact && <span className="hidden sm:inline ml-1">Print</span>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Print
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={wrapClick(printWeight)}>Weight slip only</DropdownMenuItem>
          <DropdownMenuItem onClick={wrapClick(printFull)}>{fullPrintLabel}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size={compact ? "icon" : "sm"}
            variant={compact ? "ghost" : "outline"}
            className={triggerClass}
            disabled={busy}
            title="Download weight slip PDF"
            aria-label="Download weight slip PDF"
            data-testid={`pdf-${testIdPrefix}`}
          >
            <FileDown className="h-4 w-4" />
            {!compact && <span className="hidden sm:inline ml-1">PDF</span>}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
            PDF
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={wrapClick(pdfWeight)}>Weight slip only</DropdownMenuItem>
          <DropdownMenuItem onClick={wrapClick(pdfFull)}>{fullPdfLabel}</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled className="text-[10px] text-muted-foreground">
            Full includes {opsLabel} on same page
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
